import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { scanProducts, pickWeighted, ScannedProduct } from '../utils/productScanner';
// Chilean buyers — men and women.
const NAMES = ['Javiera M.', 'Ricardo T.', 'Camila S.', 'Matías P.', 'Constanza R.', 'Sebastián G.', 'Francisca L.', 'Cristián V.', 'Antonia D.', 'Felipe A.', 'Valentina C.', 'Rodrigo H.', 'Catalina F.', 'Ignacio B.', 'Daniela N.', 'Tomás Q.', 'Josefa O.', 'Benjamín U.', 'Fernanda E.', 'Diego M.'];
// Chilean cities.
const CITIES = ['Santiago', 'Valparaíso', 'Viña del Mar', 'Concepción', 'La Serena', 'Antofagasta', 'Temuco', 'Rancagua', 'Puerto Montt', 'Iquique', 'Talca', 'Chillán', 'Arica', 'Coquimbo', 'Providencia', 'Maipú', 'Las Condes', 'Osorno', 'Punta Arenas', 'Valdivia'];
interface ToastData {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
}
const FIRST_DELAY = 3000;
const VISIBLE_MS = 4000;
const MIN_INTERVAL = 8000;
const MAX_INTERVAL = 15000;
const NAME_HISTORY_SIZE = 10;
function randomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
export function RecentPurchaseToast() {
  const [toast, setToast] = useState<ToastData | null>(null);
  const prefersReducedMotion = useReducedMotion();
  const catalogRef = useRef(scanProducts());
  const nameHistoryRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    // Rescan once after mount in case content rendered late.
    catalogRef.current = scanProducts();
    const pickName = (): string => {
      const history = nameHistoryRef.current;
      const available = NAMES.filter(n => !history.includes(n));
      const pool = available.length > 0 ? available : NAMES;
      const name = randomItem(pool);
      history.push(name);
      if (history.length > NAME_HISTORY_SIZE) history.shift();
      return name;
    };
    const showToast = () => {
      idRef.current += 1;
      const data: ToastData = {
        id: idRef.current,
        name: pickName(),
        city: randomItem(CITIES),
        product: pickWeighted(catalogRef.current)
      };
      setToast(data);
      hideTimerRef.current = setTimeout(() => {
        setToast(null);
        const interval = MIN_INTERVAL + Math.random() * (MAX_INTERVAL - MIN_INTERVAL);
        nextTimerRef.current = setTimeout(showToast, interval);
      }, VISIBLE_MS);
    };
    const startTimer = setTimeout(showToast, FIRST_DELAY);
    return () => {
      clearTimeout(startTimer);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (nextTimerRef.current) clearTimeout(nextTimerRef.current);
    };
  }, []);
  const enter = prefersReducedMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 1,
    y: 0
  };
  const from = prefersReducedMotion ? {
    opacity: 0,
    y: 0
  } : {
    opacity: 0,
    y: 40
  };
  const exit = prefersReducedMotion ? {
    opacity: 0,
    y: 0
  } : {
    opacity: 0,
    y: 40
  };
  const productLabel = 'Código del Asado Chileno';
  return <div className="fixed inset-x-0 bottom-4 z-[90] flex justify-center px-4 pointer-events-none" aria-live="polite" data-id="element-117">
      <AnimatePresence data-id="element-118">
        {toast && <motion.div key={toast.id} initial={from} animate={enter} exit={exit} transition={{
        duration: prefersReducedMotion ? 0.15 : 0.4,
        ease: 'easeOut'
      }} className="pointer-events-auto flex items-center gap-3 max-w-md w-full sm:w-auto bg-bbq-surface border border-white/10 rounded-xl shadow-2xl p-3 pr-4" data-id="element-119">
            {toast.product.thumbnailUrl ? <img src={toast.product.thumbnailUrl} alt="" aria-hidden="true" className="w-12 h-12 rounded-lg object-cover border border-white/10 shrink-0" data-id="element-120" /> : <div className="w-12 h-12 rounded-lg bg-bbq-dark border border-white/10 shrink-0" data-id="element-121" />}
            <div className="min-w-0" data-id="element-122">
              <p className="text-sm text-gray-200 leading-snug" data-id="element-123">
                <span className="font-semibold text-white" data-id="element-124">{toast.name}</span>{' '}
                en {toast.city} acaba de comprar{' '}
                <span className="font-semibold text-bbq-gold" data-id="element-125">
                  {productLabel}
                </span>
                {' — '}
                <span className="font-semibold text-white" data-id="element-126">
                  {toast.product.priceText}
                </span>
              </p>
              <p className="flex items-center gap-1 text-[11px] text-green-500 mt-0.5" data-id="element-127">
                <CheckCircle2 size={12} data-id="element-128" />
                Compra verificada
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}